import { Link, graphql, useStaticQuery } from 'gatsby'
import React from 'react'
import * as style from './index.module.css'
import { useGetCurrentPath } from '@/utils/hooks/useGetCurrentPath'
import { ALL_ROUTE } from '@/utils/const'

interface Path {
  readonly title: string
  readonly url: string
}

type Paths = Path[]

export const BreadCrumbs = (): React.JSX.Element => {
  const { currentPath } = useGetCurrentPath()

  // GraphQLでニュースとプロジェクトのデータを取得
  const data = useStaticQuery(graphql`
    query {
      allMicrocmsNews {
        edges {
          node {
            newsId
            title
          }
        }
      }
      allMicrocmsProjects {
        edges {
          node {
            projectsId
            title
          }
        }
      }
    }
  `)

  const pathnames = currentPath.split('/').filter((x) => x)

  // 'all' やページ番号は除外
  const isValidSegment = (segment: string) =>
    segment !== 'all' && isNaN(Number(segment))

  // ルートから表示用テキストを取得
  const getRouteText = (route: string): string | undefined => {
    return ALL_ROUTE.find((r) => r.route === route)?.text
  }

  // ニュースIDに対応するタイトルを取得
  const newsMatch = currentPath.match(/\/news\/([^/]+)\/?/)
  const newsTitle = newsMatch
    ? data.allMicrocmsNews.edges.find(
        ({ node }) => node.newsId === newsMatch[1],
      )?.node.title
    : undefined

  // プロジェクトIDに対応するタイトルを取得
  const projectMatch = currentPath.match(/\/projects\/([^/]+)\/?/)
  const projectTitle = projectMatch
    ? data.allMicrocmsProjects.edges.find(
        ({ node }) => node.projectsId === projectMatch[1],
      )?.node.title
    : undefined

  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

  // パスの各セグメントからリンクを生成
  const paths: Paths = pathnames.filter(isValidSegment).map((name, index) => {
    const url = `/${pathnames.slice(0, index + 1).join('/')}`

    // セグメントがニュースやプロジェクトの場合、対応するタイトルを表示
    const title =
      name === newsMatch?.[1]
        ? newsTitle || name
        : name === projectMatch?.[1]
        ? projectTitle || name
        : getRouteText(`/${name}`) || capitalize(name)

    // ニュースは `/news/all/1/`、プロジェクトは `/service` にリンク
    const adjustedUrl = name === 'news' ? '/news/all/1/' : url

    return { title, url: adjustedUrl }
  })

  // プロジェクト詳細ページの場合はServiceを追加し、Projectsをスキップ
  if (projectMatch) {
    // Serviceをパスの先頭に追加
    const servicePath = { title: 'Service', url: '/service' }
    const adjustedPaths = [servicePath] // Serviceのみを含む配列を作成
    adjustedPaths.push({
      title: projectTitle,
      url: currentPath,
    })

    return (
      <nav aria-label="Breadcrumbs">
        <ul className={style.breadcrumbList}>
          <li>
            <Link to="/">Top</Link>
          </li>
          {adjustedPaths.map((path, index) => {
            const isLast = index === adjustedPaths.length - 1
            return (
              <li key={path.title}>
                {isLast ? (
                  <span>{path.title}</span>
                ) : (
                  <Link to={path.url}>{path.title}</Link>
                )}
              </li>
            )
          })}
        </ul>
      </nav>
    )
  }

  return (
    <nav aria-label="Breadcrumbs">
      <ul className={style.breadcrumbList}>
        <li>
          <Link to="/">Top</Link>
        </li>
        {paths.map((path, index) => {
          const isLast = index === paths.length - 1
          return (
            <li key={path.title}>
              {isLast ? (
                <span>{path.title}</span>
              ) : (
                <Link to={path.url}>{path.title}</Link>
              )}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
